import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Grid,
  IconButton,
  MenuItem,
  FormControlLabel,
  Switch,
  Stack,
} from "@mui/material";
import { AddCircle, Delete } from "@mui/icons-material";
import axios from "axios";
import config from "../config";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

const baseUrl = config.baseURL;
const steps = ["Module Info", "Add Fields", "Relations", "Permissions"];

const dataTypes = [
  "text",
  "number",
  "decimal",
  "date",
  "datetime",
  "boolean",
  "dropdown",
  "file",
  "json",
  "list",
];
const displayTypes = [
  "dropdown",
  "range",
  "showOnlyRange",
  "file",
  "showOnlyDate",
  "showOnly",
  "list",
  "date",
  "showOnlyText",
  "textDetails",
  "icon",
  "normalTableCell",
  "input",
  "textarea",
];

const ModuleBuilder = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [userData, setUserData] = useState(null);
  const navigate = useNavigate();

  // Step 1: Module info
  const [moduleData, setModuleData] = useState({
    slug: "",
    name: "",
    description: "",
    is_active: true,
  });

  // Step 2: Fields
  const [fields, setFields] = useState([
    {
      accessor: "",
      label: "",
      data_type: "text",
      display_type: "input",
      required: false,
      sortable: false,
      filterable: false,
      readonly: false,
      order_index: 0,
    },
  ]);

  // Step 3: Relations
  const [relations, setRelations] = useState([]);

  // Step 4: Permissions
  const [permissions, setPermissions] = useState([]);

  const [loading, setLoading] = useState(false);
  const [modulesList, setModulesList] = useState([]);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserData(decoded);
      } catch (e) {
        console.error("Invalid token");
      }
    }

    fetchModulesAndRoles();
  }, []);

  const fetchModulesAndRoles = async () => {
    try {
      const resModules = await axios.get(`${baseUrl}/api/modules`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const resRoles = await axios.get(`${baseUrl}/api/role`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setModulesList(resModules.data);
      setRoles(resRoles.data);
    } catch (err) {
      console.error("Error loading modules/role", err);
    }
  };

  const handleAddField = () => {
    setFields([
      ...fields,
      {
        accessor: "",
        label: "",
        data_type: "text",
        display_type: "input",
        required: false,
        sortable: false,
        filterable: false,
        readonly: false,
        order_index: fields.length,
      },
    ]);
  };

  const handleRemoveField = (index) => {
    setFields(fields.filter((_, i) => i !== index));
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      await saveModule();
    } else if (activeStep === 1) {
      await saveFields();
    } else if (activeStep === 2) {
      await saveRelations();
    } else if (activeStep === 3) {
      await savePermissions();
    }
  };

  const saveModule = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${baseUrl}/api/module`, moduleData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setModuleData((prev) => ({ ...prev, id: res.data.id }));
      setActiveStep(1);
    } catch (err) {
      console.error("Error creating module:", err);
      alert("Failed to create module");
    } finally {
      setLoading(false);
    }
  };

  const saveFields = async () => {
    if (!moduleData.id) return alert("Module not saved yet");
    setLoading(true);
    try {
      await axios.post(
        `${baseUrl}/api/modules/${moduleData.id}/fields`,
        { fields },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActiveStep(2);
    } catch (err) {
      console.error(err);
      alert("Failed to save fields");
    } finally {
      setLoading(false);
    }
  };

  const saveRelations = async () => {
    if (!moduleData.id) return alert("Module not saved yet");
    setLoading(true);
    try {
      await axios.post(
        `${baseUrl}/api/modules/${moduleData.id}/relations`,
        { relations },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActiveStep(3);
    } catch (err) {
      console.error(err);
      alert("Failed to save relations");
    } finally {
      setLoading(false);
    }
  };

  const savePermissions = async () => {
    if (!moduleData.id) return alert("Module not saved yet");
    setLoading(true);
    try {
      await axios.post(
        `${baseUrl}/api/modules/${moduleData.id}/permissions`,
        { permissions },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Module created successfully!");
      navigate("/dashboard/modules");
    } catch (err) {
      console.error(err);
      alert("Failed to save permissions");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: "bold", mb: 3 }}>
        Create New Module
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* STEP 1: Module Info */}
      {activeStep === 0 && (
        <Card>
          <CardContent>
            <Grid container spacing={2} direction="column">
              {/* Display Name */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Display Name"
                  value={moduleData.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    const slug = name
                      .toLowerCase()
                      .trim()
                      .replace(/\s+/g, "-")
                      .replace(/[^a-z0-9-]/g, ""); // auto-generate slug
                    setModuleData({ ...moduleData, name, slug });
                  }}
                />
              </Grid>

              {/* Module Slug (auto-filled, disabled) */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Module Slug"
                  value={moduleData.slug}
                  disabled
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={moduleData.description}
                  onChange={(e) =>
                    setModuleData({
                      ...moduleData,
                      description: e.target.value,
                    })
                  }
                />
              </Grid>

              {/* Active Toggle */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={moduleData.is_active}
                      onChange={(e) =>
                        setModuleData({
                          ...moduleData,
                          is_active: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* STEP 2: Fields */}
      {activeStep === 1 && (
        <Box>
          {fields.map((field, idx) => (
            <Card key={idx} sx={{ mb: 2 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Accessor"
                      fullWidth
                      value={field.accessor}
                      onChange={(e) =>
                        setFields((prev) =>
                          prev.map((f, i) =>
                            i === idx ? { ...f, accessor: e.target.value } : f
                          )
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Label"
                      fullWidth
                      value={field.label}
                      onChange={(e) =>
                        setFields((prev) =>
                          prev.map((f, i) =>
                            i === idx ? { ...f, label: e.target.value } : f
                          )
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      select
                      label="Data Type"
                      fullWidth
                      value={field.data_type}
                      onChange={(e) =>
                        setFields((prev) =>
                          prev.map((f, i) =>
                            i === idx ? { ...f, data_type: e.target.value } : f
                          )
                        )
                      }
                    >
                      {dataTypes.map((d) => (
                        <MenuItem key={d} value={d}>
                          {d}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      select
                      label="Display Type"
                      fullWidth
                      value={field.display_type}
                      onChange={(e) =>
                        setFields((prev) =>
                          prev.map((f, i) =>
                            i === idx
                              ? { ...f, display_type: e.target.value }
                              : f
                          )
                        )
                      }
                    >
                      {displayTypes.map((d) => (
                        <MenuItem key={d} value={d}>
                          {d}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveField(idx)}
                    >
                      <Delete />
                    </IconButton>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}

          <Button
            startIcon={<AddCircle />}
            onClick={handleAddField}
            sx={{ mt: 2 }}
          >
            Add Field
          </Button>
        </Box>
      )}

      {activeStep === 2 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Define Relations (optional)
          </Typography>
          {fields.map((field, idx) => (
            <Card key={idx} sx={{ mb: 2 }}>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Field"
                      fullWidth
                      value={field.label || field.accessor}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Related Module"
                      value={
                        relations.find((r) => r.field === field.accessor)
                          ?.related_module_id || ""
                      }
                      onChange={(e) => {
                        const selectedModuleId = e.target.value;
                        setRelations((prev) => {
                          const existing = prev.filter(
                            (r) => r.field !== field.accessor
                          );
                          return [
                            ...existing,
                            {
                              field: field.accessor,
                              related_module_id: selectedModuleId,
                            },
                          ];
                        });
                      }}
                    >
                      <MenuItem value="">None</MenuItem>
                      {modulesList.map((mod) => (
                        <MenuItem key={mod.id} value={mod.id}>
                          {mod.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <Typography variant="body2" color="text.secondary">
                      {relations.find((r) => r.field === field.accessor)
                        ? "Linked"
                        : "No Relation"}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {activeStep === 3 && (
        <Box>
          {roles.map((role, idx) => (
            <Card key={idx} sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6">{role.name}</Typography>
                <Stack direction="row" spacing={2} mt={2}>
                  {["view", "create", "edit", "delete"].map((perm) => (
                    <FormControlLabel
                      key={perm}
                      control={
                        <Switch
                          checked={
                            permissions.find(
                              (p) =>
                                p.role_id === role.id && p.permission === perm
                            )?.enabled || false
                          }
                          onChange={(e) => {
                            setPermissions((prev) => {
                              const updated = prev.filter(
                                (p) =>
                                  !(
                                    p.role_id === role.id &&
                                    p.permission === perm
                                  )
                              );
                              updated.push({
                                role_id: role.id,
                                permission: perm,
                                enabled: e.target.checked,
                              });
                              return updated;
                            });
                          }}
                        />
                      }
                      label={perm}
                    />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
        {activeStep > 0 && (
          <Button
            variant="outlined"
            onClick={() => setActiveStep(activeStep - 1)}
          >
            Back
          </Button>
        )}
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#EA6A12",
            "&:hover": { backgroundColor: "#c7570f" },
          }}
          onClick={handleNext}
          disabled={loading}
        >
          {activeStep === steps.length - 1 ? "Finish" : "Next"}
        </Button>
      </Stack>
    </Box>
  );
};

export default ModuleBuilder;
